import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Row, Col, Select, Switch } from 'antd';
import { BaseOptionType } from 'antd/es/select';
import { useDispatch, useSelector } from 'react-redux';
import {
  useForm,
  Controller,
  Control,
  FieldErrors,
  FieldValues,
} from 'react-hook-form';
import {
  AlertBox,
  Button,
  CountrySelect,
  Loader,
  ModalInfo,
  OptionAlert,
} from '@/components';
import {
  AlertRule,
  AlertRuleCommonParams,
  AlertRuleType,
  AlertRuleIntervalType,
  AlertRuleDirection,
  AlertRuleCategoryJoin,
  CompRiskLevel,
} from '@/api';
import { RiskKeys } from '@/config';
import { sanitizeNumericInput } from '@/helpers';
import { alertRulesActions, complianceListsActions } from '@/redux/actions';
import {
  alertRulesSelectors,
  complianceAlertLevelsSelectors,
  complianceListsSelectors,
} from '@/redux/selectors';
import { AlertRuleFormFields } from '../AlertRuleForm/AlertRuleFormFields';
import {
  selectPopupStyles,
  DEFAULT_COMMON_RULE,
  type AlertGradeOption,
  type AlertDirectionOption,
  type AlertEntityTypeOption,
} from '../AlertRuleForm/constants';
import { buildAlertRuleCommonParams } from '../AlertRuleForm/helpers';
import styles from './CommonRuleModal.module.sass';

const renderOptionAlert = (option: BaseOptionType) => (
  <OptionAlert value={option.value} />
);

type CommonRuleFormValues = {
  name: string;
  risk: CompRiskLevel;
  direction: AlertRuleDirection;
  categories: RiskKeys[];
  categoryJoin: AlertRuleCategoryJoin;
  params: {
    minVolume?: string;
    countries?: string[];
    isSuspended?: boolean;
    isBlacklisted?: boolean;
    targetCustomerRisk?: CompRiskLevel;
    inactivityType?: AlertRuleIntervalType;
    inactivityValue?: string;
    intervalType?: AlertRuleIntervalType;
    intervalValue?: string;
  };
};

interface CommonRuleModalProps {
  isOpen: boolean;
  isLoading: boolean;
  editRuleId: AlertRule['id'] | null;
  onClose: () => void;
}

export const CommonRuleModal = ({
  isOpen,
  isLoading,
  editRuleId,
  onClose,
}: CommonRuleModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isAdd = !editRuleId;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const complianceListsData = useSelector(
    complianceListsSelectors.getComplianceListsSlice,
  );
  const countries: Record<string, string> =
    complianceListsData?.data?.countries ?? {};

  const alertRulesData = useSelector(alertRulesSelectors.getAlertRulesData);
  const alertRulesError = useSelector(alertRulesSelectors.getAlertRulesError);
  const complianceAlertLevelsData = useSelector(
    complianceAlertLevelsSelectors.getComplianceAlertLevelsSlice,
  );

  const editRuleData = useMemo(() => {
    if (!editRuleId) return null;
    return alertRulesData.data?.find(
      (rule: AlertRule) => String(rule.id) === String(editRuleId),
    );
  }, [editRuleId, alertRulesData]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CommonRuleFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      risk: CompRiskLevel.Low,
      direction: 'income',
      categories: [RiskKeys.MERCHANT_SERVICES],
      categoryJoin: 'all',
      params: {
        minVolume: '',
        countries: [],
        isSuspended: false,
        isBlacklisted: false,
        inactivityValue: '',
        inactivityType: 'hour',
        intervalType: 'minute',
        intervalValue: '',
      },
    },
  });

  useEffect(() => {
    if (!isOpen) setIsSubmitting(false);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      dispatch(complianceListsActions.fetchLists());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (
      editRuleData &&
      editRuleId &&
      editRuleData.type === AlertRuleType.COMMON
    ) {
      const commonParams = editRuleData.params as AlertRuleCommonParams;
      const editCategories =
        (editRuleData.categories?.length ?? 0) > 0
          ? editRuleData.categories!
          : DEFAULT_COMMON_RULE.categories ?? [];

      const rawParams = editRuleData.params as AlertRuleCommonParams & {
        country?: string;
      };
      const editCountries =
        rawParams.countries ?? (rawParams.country ? [rawParams.country] : []);

      reset({
        name: editRuleData.name,
        risk: editRuleData.risk,
        direction: editRuleData.direction,
        categories: editCategories,
        categoryJoin: editRuleData.categoryJoin ?? 'all',
        params: {
          minVolume: commonParams?.minVolume
            ? String(commonParams.minVolume)
            : '',
          countries: editCountries,
          isSuspended: commonParams?.isSuspended ?? false,
          isBlacklisted: commonParams?.isBlacklisted ?? false,
          targetCustomerRisk: commonParams?.targetCustomerRisk,
          inactivityType: commonParams?.inactivityType ?? 'hour',
          inactivityValue: commonParams?.inactivityValue
            ? String(commonParams.inactivityValue)
            : '',
          intervalType: commonParams?.intervalType ?? 'minute',
          intervalValue: commonParams?.intervalValue
            ? String(commonParams.intervalValue)
            : '',
        },
      });
      return;
    }

    if (!editRuleId) {
      reset({
        name: DEFAULT_COMMON_RULE.name,
        risk: DEFAULT_COMMON_RULE.risk,
        direction: DEFAULT_COMMON_RULE.direction,
        categories: DEFAULT_COMMON_RULE.categories ?? [],
        categoryJoin: DEFAULT_COMMON_RULE.categoryJoin ?? 'all',
        params: {
          minVolume: '',
          countries: [],
          isSuspended: false,
          isBlacklisted: false,
          inactivityValue: '',
          inactivityType: 'hour',
          intervalType: 'minute',
          intervalValue: '',
        },
      });
    }
  }, [editRuleId, editRuleData, reset]);

  useEffect(() => {
    if (isSubmitting && !isLoading && !alertRulesError) {
      setIsSubmitting(false);
      reset();
      onClose();
    }
  }, [isSubmitting, isLoading, alertRulesError, reset, onClose]);

  const alertGradeOptions: AlertGradeOption[] = [
    { label: t('translation.comp_risk_label_low'), value: CompRiskLevel.Low },
    {
      label: t('translation.comp_risk_label_medium'),
      value: CompRiskLevel.Medium,
    },
    { label: t('translation.comp_risk_label_high'), value: CompRiskLevel.High },
    {
      label: t('translation.comp_risk_label_severe'),
      value: CompRiskLevel.Severe,
    },
  ];

  const alertDirectionOptions: AlertDirectionOption[] = [
    { label: t('translation.all'), value: 'all' },
    { label: `${t('translation.deposit')} ↓`, value: 'income' },
    { label: `${t('translation.withdrawal')} ↑`, value: 'outcome' },
  ];

  const alertEntityTypeOptions: AlertEntityTypeOption[] = (
    complianceAlertLevelsData?.data?.categories ?? []
  ).map((key) => ({ label: key, value: key }));

  const intervalOptions = [
    { value: 'minute', label: t('translation.minute') },
    { value: 'hour', label: t('translation.hour') },
    { value: 'day', label: t('translation.day') },
  ];

  const onSubmit = useCallback(
    (data: CommonRuleFormValues) => {
      setIsSubmitting(true);
      const payload = {
        name: data.name,
        risk: data.risk,
        direction: data.direction,
        categories: data.categories,
        categoryJoin: data.categoryJoin,
        type: AlertRuleType.COMMON,
        params: buildAlertRuleCommonParams(data.params),
      };
      if (isAdd) {
        dispatch(alertRulesActions.createAlertRule(payload));
      } else if (editRuleId) {
        dispatch(
          alertRulesActions.updateAlertRule({ id: editRuleId, ...payload }),
        );
      }
    },
    [isAdd, editRuleId, dispatch],
  );

  const handleOnSave = handleSubmit(onSubmit);

  return (
    <ModalInfo
      isOpen={isOpen}
      canClose={!isLoading}
      title={isAdd ? t('translation.add_rule') : t('translation.edit_rule')}
      actions={
        <div className={styles.actions}>
          <Button
            size="s"
            type="secondary"
            corner="rect"
            minWidth={92}
            onClick={onClose}
          >
            {t('translation.cancel_btn')}
          </Button>
          <Button
            size="s"
            type="primary"
            corner="rect"
            minWidth={92}
            onClick={handleOnSave}
          >
            {isAdd ? t('translation.add') : t('translation.save')}
          </Button>
        </div>
      }
      className={styles.modal}
      onClose={onClose}
    >
      {alertRulesError && (
        <AlertBox type="danger">{{ description: alertRulesError }}</AlertBox>
      )}

      <div className={styles.form}>
        <AlertRuleFormFields
          control={control as unknown as Control<FieldValues>}
          errors={errors as unknown as FieldErrors<FieldValues>}
          alertGradeOptions={alertGradeOptions}
          alertDirectionOptions={alertDirectionOptions}
          alertEntityTypeOptions={alertEntityTypeOptions}
          isShowBlacklistedField
        />

        <div className={styles.group}>
          <Row gutter={20}>
            <Col xs={24} lg={24}>
              <div className={styles.field}>
                <div className={styles.label}>
                  {t('translation.min_amount')}
                </div>
                <Controller
                  name="params.minVolume"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      type="number"
                      min={0}
                      onChange={(e) => sanitizeNumericInput(e, field.onChange)}
                    />
                  )}
                />
              </div>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col xs={24} lg={12}>
              <div className={styles.field}>
                <div className={styles.label}>
                  {t('translation.inactivity_interval')}
                </div>
                <div className={styles.fieldRow}>
                  <Controller
                    name="params.inactivityValue"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        size="large"
                        type="number"
                        min={0}
                        onChange={(e) =>
                          sanitizeNumericInput(e, field.onChange)
                        }
                      />
                    )}
                  />
                  <Controller
                    name="params.inactivityType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        size="large"
                        options={intervalOptions}
                        allowClear
                        styles={selectPopupStyles}
                        className={styles.selectWhite}
                      />
                    )}
                  />
                </div>
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div className={styles.field}>
                <div className={styles.label}>
                  {t('translation.time_period')}
                </div>
                <div className={styles.fieldRow}>
                  <Controller
                    name="params.intervalValue"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        size="large"
                        type="number"
                        min={0}
                        onChange={(e) =>
                          sanitizeNumericInput(e, field.onChange)
                        }
                      />
                    )}
                  />
                  <Controller
                    name="params.intervalType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        size="large"
                        options={intervalOptions}
                        styles={selectPopupStyles}
                        className={styles.selectWhite}
                      />
                    )}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <div className={styles.group}>
          <Row gutter={20}>
            <Col xs={24} lg={12}>
              <div className={styles.field}>
                <div className={styles.label}>
                  {t('translation.target_customer_risk')}
                </div>
                <Controller
                  name="params.targetCustomerRisk"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      size="large"
                      options={alertGradeOptions}
                      styles={selectPopupStyles}
                      popupMatchSelectWidth={false}
                      labelRender={renderOptionAlert}
                      optionRender={renderOptionAlert}
                      className={styles.selectWhite}
                      allowClear
                    />
                  )}
                />
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div className={styles.field}>
                <Controller
                  name="params.isSuspended"
                  control={control}
                  render={({ field }) => (
                    <label className={styles.switchRow}>
                      <Switch
                        checked={!!field.value}
                        onChange={field.onChange}
                      />
                      <span className={styles.label}>
                        {t('translation.suspended')}
                      </span>
                    </label>
                  )}
                />
              </div>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col xs={24} lg={24}>
              <div className={styles.field}>
                <div className={styles.label}>{t('translation.countries')}</div>
                <Controller
                  name="params.countries"
                  control={control}
                  render={({ field }) => (
                    <CountrySelect
                      countries={countries}
                      value={field.value ?? []}
                      onChange={(v) => field.onChange(v ?? [])}
                      className={styles.selectWhite}
                      isMultiple
                    />
                  )}
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {isLoading && (
        <div className={styles.loader}>
          <Loader size="medium" />
        </div>
      )}
    </ModalInfo>
  );
};
